import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Link2, Loader2, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight,
  Sparkles, Star, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { inventoryAPI } from '../../api';
import InventoryProductForm, {
  validateProduct,
  isValid,
} from '../../components/inventory/InventoryProductForm';

const SAVE_COST_PER_PRODUCT = 2;

// =====================================================================
// Top-of-modal stepper
// =====================================================================
// Internal "extracting" maps to the Review pip (active, loading) since the
// extraction is the work being done to populate the Review screen.
const STEP_PIP_INDEX = { url: 0, extracting: 1, review: 1, done: 2 };
const Stepper = ({ step }) => {
  const labels = ['URL', 'Review', 'Done'];
  const idx = STEP_PIP_INDEX[step] ?? 0;
  const loading = step === 'extracting';
  return (
    <div className="flex items-center gap-2 px-1 mb-4">
      {labels.map((l, i) => (
        <React.Fragment key={l}>
          <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= idx ? 'text-blue-600' : 'text-slate-400'}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              i < idx ? 'bg-blue-600 text-white'
              : i === idx ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-600'
              : 'bg-slate-100 text-slate-500'}`}>
              {i < idx ? '✓' : (i === idx && loading) ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : i + 1}
            </div>
            <span className="hidden sm:inline">{l}</span>
          </div>
          {i < labels.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? 'bg-blue-600' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// =====================================================================
// LEFT panel — Photos + Try-On selector
// =====================================================================
const PhotosPanel = ({ product, onUpdate, validationError }) => {
  const sel = product._selected_images || [];
  const tryon = product._tryon_image_url || (sel.length === 1 ? sel[0] : null);

  const toggleSelect = (url) => {
    let next;
    if (sel.includes(url)) next = sel.filter((x) => x !== url);
    else if (sel.length < 5) next = [...sel, url];
    else { toast.error('Maximum 5 images per product'); return; }
    // If we removed the current try-on or only one remains, auto-set
    let nextTryon = tryon;
    if (!next.includes(tryon)) nextTryon = next[0] || null;
    if (next.length === 1) nextTryon = next[0];
    onUpdate({ _selected_images: next, _tryon_image_url: nextTryon });
  };

  const setTryon = (url) => onUpdate({ _tryon_image_url: url });

  const heroImage = tryon || sel[0] || product.images?.[0];

  return (
    <div className="bg-slate-50 border-r border-slate-200 flex flex-col h-full min-h-0">
      {/* Hero preview — capped so it never eats the thumbnail strip on short screens */}
      <div className="aspect-[3/4] max-h-[40vh] bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
        {heroImage ? (
          <img src={heroImage} alt={product.product_name} className="w-full h-full object-contain" />
        ) : (
          <div className="text-slate-300 text-xs">No photo</div>
        )}
      </div>
      {/* Thumbnail strip */}
      <div className="p-3 flex-1 overflow-y-auto min-h-0">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-medium text-slate-600">
            Photos <span className="text-slate-400">({sel.length}/5)</span>
          </p>
          {sel.length > 1 && (
            <p className="text-[10px] text-slate-500">
              <Star className="w-3 h-3 inline -mt-0.5" /> = Try-on
            </p>
          )}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {(product.images || []).slice(0, 12).map((url) => {
            const isSel = sel.includes(url);
            const isTry = tryon === url;
            return (
              <div key={url} className="relative group">
                <button type="button" onClick={() => toggleSelect(url)}
                  className={`relative w-full aspect-square overflow-hidden rounded-md border-2 transition-all ${
                    isSel ? 'border-blue-500 ring-1 ring-blue-200' : 'border-transparent hover:border-slate-300'}`}
                  data-testid={`photo-thumb-${url.slice(-12)}`}>
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {isSel && (
                    <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </button>
                {isSel && sel.length > 1 && (
                  <button type="button" onClick={() => setTryon(url)}
                    className={`absolute bottom-0.5 left-0.5 p-1 rounded-full transition-all ${
                      isTry ? 'bg-amber-400 text-white' : 'bg-white/90 text-slate-400 hover:text-amber-500 opacity-0 group-hover:opacity-100'}`}
                    title="Set as Try-On image">
                    <Star className="w-3 h-3" fill={isTry ? 'currentColor' : 'none'} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {validationError && <p className="text-[11px] text-red-500 mt-2">{validationError}</p>}
      </div>
    </div>
  );
};

// =====================================================================
// Main modal
// =====================================================================
const ImportFromUrlModal = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState('url');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState('page');
  const [products, setProducts] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [savedSummary, setSavedSummary] = useState({ saved: 0, failed: 0, credits: 0 });
  const [discoverError, setDiscoverError] = useState(null);
  // Progressive extraction UI state
  const [progressSteps, setProgressSteps] = useState([]); // [{id,text,status:'pending'|'active'|'done'|'error'}]
  const [extractedCount, setExtractedCount] = useState(0);
  const [totalUrls, setTotalUrls] = useState(0);
  const [extractError, setExtractError] = useState(null);

  const reset = () => {
    setStep('url'); setUrl(''); setMode('page');
    setProducts([]); setReviewIdx(0);
    setSavedSummary({ saved: 0, failed: 0, credits: 0 });
    setBusy(false); setDiscoverError(null);
    setProgressSteps([]); setExtractedCount(0); setTotalUrls(0); setExtractError(null);
  };
  const close = () => { if (!busy) { reset(); onClose(); } };

  // Helpers for the progress list
  const upsertStep = useCallback((id, patch) => {
    setProgressSteps((prev) => {
      const i = prev.findIndex((s) => s.id === id);
      if (i === -1) return [...prev, { id, status: 'active', text: '', ...patch }];
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }, []);

  // ---------- Helper: enrich extracted product with review-time defaults ----------
  const enrichProduct = (p) => ({
    ...p,
    assigned_moment: null,
    additional_moments: [],
    dress_type: '',          // Mandatory — partner picks
    free_size: false,
    sku_id: p.sku_id_preview || '',
    gender: p.gender || 'female',
    color_variants: [],
    color_change_enabled: false,
    color_change_quality: 'standard',
    size_chart: null,
    _selected_images: (p.images || []).slice(0, 5),
    _tryon_image_url: (p.images || []).length === 1 ? p.images[0] : null,
  });

  // ---------- Single combined action: Discover → Extract → Review ----------
  const runImport = async () => {
    if (!url.trim()) { toast.error('Enter a URL'); return; }

    setBusy(true);
    setDiscoverError(null);
    setExtractError(null);
    setProgressSteps([]);
    setExtractedCount(0);
    setTotalUrls(0);
    setStep('extracting');

    // ----- Phase 1: discover -----
    upsertStep('discover', {
      status: 'active',
      text: mode === 'sitemap' ? 'Reading sitemap and collecting product links…' : 'Reading the page and finding product links…',
    });

    let discData;
    try {
      const { data } = mode === 'sitemap'
        ? await inventoryAPI.importSitemap(url.trim())
        : await inventoryAPI.importDiscover(url.trim());
      discData = data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : detail;
      upsertStep('discover', { status: 'error' });
      setExtractError(msg || e.message || 'Discovery failed. Please check the URL and try again.');
      setBusy(false);
      return;
    }

    if (!discData.success || !discData.urls?.length) {
      upsertStep('discover', { status: 'error' });
      const msg = discData.message || discData.error || 'No product links found on this page.';
      setExtractError(msg);
      // Surface SPA-detection hint back on the URL screen if user retries
      setDiscoverError({
        message: msg,
        hint: discData.hint,
        spaDetected: !!discData.spa_detected,
        onSwitchToSitemap: discData.spa_detected && mode === 'page'
          ? () => { setMode('sitemap'); setExtractError(null); setStep('url'); } : null,
      });
      setBusy(false);
      return;
    }
    if (discData.via_sitemap) toast.success(`Used sitemap: ${discData.via_sitemap}`);

    upsertStep('discover', {
      status: 'done',
      text: `Found ${discData.urls.length} product link${discData.urls.length === 1 ? '' : 's'}`,
    });

    // ----- Fast path: pre-extracted from a structured API or XHR capture -----
    if (discData.pre_extracted_products?.length) {
      const via = discData.via_render || '';
      let captureText;
      if (via === 'shopify-products-json') {
        captureText = `Captured ${discData.pre_extracted_products.length} products from Shopify's catalog API`;
      } else if (via === 'woocommerce-store-api') {
        captureText = `Captured ${discData.pre_extracted_products.length} products from WooCommerce's Store API`;
      } else {
        captureText = `Captured ${discData.pre_extracted_products.length} products directly from the site's API`;
      }
      upsertStep('capture', { status: 'done', text: captureText });
      const enriched = discData.pre_extracted_products.map(enrichProduct);
      setTotalUrls(enriched.length);
      setExtractedCount(enriched.length);
      setProducts(enriched);
      // Tiny "ready" pause for UX continuity
      upsertStep('ready', { status: 'done', text: 'Ready for review' });
      setTimeout(() => setStep('review'), 400);
      setBusy(false);
      return;
    }

    // ----- Phase 2: reserve credits silently -----
    upsertStep('prepare', { status: 'active', text: 'Preparing extraction workspace…' });
    let reservation;
    try {
      const { data: r } = await inventoryAPI.importReserve(discData.urls, url);
      reservation = r;
      upsertStep('prepare', { status: 'done', text: 'Workspace ready' });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : detail;
      upsertStep('prepare', { status: 'error' });
      setExtractError(msg || 'Could not start extraction. Please try again.');
      setBusy(false);
      return;
    }

    // ----- Phase 3: extract in batches with live progress -----
    setTotalUrls(discData.urls.length);
    upsertStep('extract', {
      status: 'active',
      text: `Extracting product details (0 of ${discData.urls.length})…`,
    });

    const allUrls = discData.urls;
    const batchSize = 5;
    let totalT3 = 0;
    const acc = [];
    try {
      for (let i = 0; i < allUrls.length; i += batchSize) {
        const slice = allUrls.slice(i, i + batchSize);
        const { data } = await inventoryAPI.importExtractBatch(slice, reservation.reservation_id);
        totalT3 += data.tier3_calls || 0;
        const enriched = (data.products || []).map(enrichProduct);
        acc.push(...enriched);
        setProducts([...acc]);
        setExtractedCount(acc.length);
        upsertStep('extract', {
          status: 'active',
          text: `Extracting product details (${acc.length} of ${allUrls.length})…`,
        });
      }
      upsertStep('extract', {
        status: 'done',
        text: `Extracted ${acc.length} product${acc.length === 1 ? '' : 's'}`,
      });
      // Settle (refund unused render credits) — silent
      try { await inventoryAPI.importSettle(reservation.reservation_id, totalT3); } catch { /* non-fatal */ }
      upsertStep('ready', { status: 'done', text: 'Ready for review' });
      setTimeout(() => setStep('review'), 400);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : detail;
      upsertStep('extract', { status: 'error' });
      // Settle whatever we did to refund correctly
      try { await inventoryAPI.importSettle(reservation.reservation_id, totalT3); } catch { /* non-fatal */ }
      if (acc.length > 0) {
        // Partial success — let the partner review what we got
        toast.error(`Some products failed to extract — showing the ${acc.length} we got`);
        setTimeout(() => setStep('review'), 400);
      } else {
        setExtractError(msg || 'Extraction failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const retryExtraction = () => {
    setStep('url');
    setExtractError(null);
    setProgressSteps([]);
  };

  // ---------- Update / nav helpers ----------
  const updateProduct = (idx, patch) => {
    setProducts((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const goPrev = () => setReviewIdx((i) => Math.max(0, i - 1));
  const goNext = () => setReviewIdx((i) => Math.min(products.length - 1, i + 1));

  // ---------- Save / Skip a single product (per-product 2-credit charge) ----------
  const skipCurrent = () => {
    if (reviewIdx < products.length - 1) goNext();
    else finishReview();
  };

  const saveCurrent = async () => {
    const cur = products[reviewIdx];
    const errs = validateProduct(cur);
    if (Object.keys(errs).length) { toast.error('Please fill all required fields'); return; }
    const payload = {
      sku_id: cur.sku_id || undefined,
      product_name: cur.product_name || '',
      category: cur.category || '',
      description: cur.description || '',
      dress_type: cur.dress_type,
      assigned_moment: cur.assigned_moment || null,
      additional_moments: cur.additional_moments || [],
      gender: cur.gender || 'female',
      fabric: cur.fabric || '',
      color: cur.color_variants?.[0]?.name || cur.color || '',
      color_variants: cur.color_variants || [],
      mrp: cur.mrp ?? null,
      selling_price: cur.selling_price ?? null,
      currency: cur.currency || 'INR',
      sizes: cur.free_size ? [] : Object.keys(cur.size_chart || {}),
      free_size: !!cur.free_size,
      size_chart: cur.size_chart || null,
      images: cur._selected_images || [],
      tryon_image_url: cur._tryon_image_url || (cur._selected_images || [])[0] || null,
      source_url: cur.source_url || null,
      color_change_enabled: !!cur.color_change_enabled,
      color_change_quality: cur.color_change_quality || 'standard',
    };
    setBusy(true);
    try {
      const { data } = await inventoryAPI.importSaveBatch([payload]);
      if (data.saved_count > 0) {
        toast.success(`Saved! −${data.credits_charged} credits (balance: ${data.wallet_balance.toFixed(2)})`);
        setSavedSummary((s) => ({
          saved: s.saved + data.saved_count,
          failed: s.failed + data.failed_count,
          credits: s.credits + (data.credits_charged || 0),
        }));
        // Mark as saved so user can't double-save
        updateProduct(reviewIdx, { _saved: true });
        if (reviewIdx < products.length - 1) goNext();
        else finishReview();
      } else {
        toast.error(`Save failed: ${data.failed?.[0]?.error || 'unknown'}`);
        setSavedSummary((s) => ({ ...s, failed: s.failed + (data.failed_count || 1) }));
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : detail;
      toast.error(msg || 'Save failed');
    } finally { setBusy(false); }
  };

  const finishReview = () => setStep('done');

  // ---------- Render ----------
  if (!open) return null;
  const current = products[reviewIdx];
  const currentErrors = current ? validateProduct(current) : {};
  const currentValid = current ? isValid(current) : false;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={close} data-testid="import-url-modal">
        <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
          className={`bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col ${
            step === 'review' ? 'max-w-6xl h-[92vh] min-h-[560px]' : step === 'extracting' ? 'max-w-2xl h-[560px]' : 'max-w-3xl max-h-[90vh]'}`}
          onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Import from Website</h2>
                <p className="text-xs text-slate-500">
                  {step === 'review' && current
                    ? `${reviewIdx + 1} of ${products.length} • ${savedSummary.saved} saved • ${savedSummary.credits} credits used`
                    : step === 'extracting'
                    ? 'Pulling product details from your site…'
                    : 'Paste any product or category URL'}
                </p>
              </div>
            </div>
            <button onClick={close} disabled={busy}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              data-testid="import-modal-close-btn">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Stepper (hide on review for cleanest UX) */}
          {step !== 'review' && (
            <div className="px-5 pt-4 flex-shrink-0">
              <Stepper step={step} />
            </div>
          )}

          {/* ============ STEP CONTENT ============ */}
          <div className="flex-1 overflow-hidden">
            {step === 'url' && (
              <UrlStep url={url} setUrl={setUrl} mode={mode} setMode={setMode}
                busy={busy} onDiscover={runImport}
                discoverError={discoverError} setDiscoverError={setDiscoverError} />
            )}

            {step === 'extracting' && (
              <ExtractingStep
                steps={progressSteps}
                extracted={extractedCount}
                total={totalUrls}
                error={extractError}
                onBack={retryExtraction}
                onRetry={runImport}
              />
            )}

            {step === 'review' && current && (
              <ReviewStep product={current} idx={reviewIdx} total={products.length}
                errors={currentErrors} valid={currentValid} busy={busy}
                onUpdate={(patch) => updateProduct(reviewIdx, patch)}
                onPrev={goPrev} onNext={goNext}
                onSkip={skipCurrent} onSave={saveCurrent}
                onFinish={finishReview} />
            )}

            {step === 'done' && (
              <DoneStep summary={savedSummary} onView={() => { onComplete?.(); close(); }} onClose={close} />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =====================================================================
// STEP 1 — URL input
// =====================================================================
const UrlStep = ({ url, setUrl, mode, setMode, busy, onDiscover, discoverError, setDiscoverError }) => (
  <div className="px-5 py-4 overflow-y-auto h-full space-y-4">
    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
      <button type="button" onClick={() => setMode('page')} disabled={busy}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          mode === 'page' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
        data-testid="import-mode-page">
        <Link2 className="w-3.5 h-3.5" /> Page URL
      </button>
      <button type="button" onClick={() => setMode('sitemap')} disabled={busy}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          mode === 'sitemap' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
        data-testid="import-mode-sitemap">
        <Sparkles className="w-3.5 h-3.5" /> Sitemap (bulk)
      </button>
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {mode === 'sitemap' ? 'Sitemap URL' : 'Product or category URL'}
      </label>
      <div className="relative">
        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="url" value={url}
          onChange={(e) => { setUrl(e.target.value); setDiscoverError(null); }}
          placeholder={mode === 'sitemap'
            ? 'https://www.example.com/sitemap.xml'
            : 'https://www.myntra.com/.../buy or your store category page'}
          className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          disabled={busy} data-testid="import-url-input" />
      </div>
    </div>

    {discoverError && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2"
        data-testid="import-discover-error">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 space-y-1.5">
            <p className="font-medium">{discoverError.message}</p>
            {discoverError.hint && <pre className="whitespace-pre-wrap font-sans text-amber-800/90">{discoverError.hint}</pre>}
          </div>
        </div>
        {discoverError.onSwitchToSitemap && (
          <button onClick={discoverError.onSwitchToSitemap}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-md text-xs font-medium hover:bg-amber-100"
            data-testid="import-switch-to-sitemap">
            <Sparkles className="w-3.5 h-3.5" /> Try sitemap mode instead
          </button>
        )}
      </div>
    )}

    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1.5">
      <p className="font-medium text-slate-700">How it works</p>
      <ul className="space-y-1 list-disc pl-4">
        <li>Paste a product URL or category page — we'll find every product on it.</li>
        <li>Modern interactive sites are auto-detected; we render them like a real browser.</li>
        <li>Review each product, edit anything that's off, then save the ones you want.</li>
        <li>You only pay <b>{SAVE_COST_PER_PRODUCT} credits per product saved</b> — skip is always free.</li>
      </ul>
    </div>

    <button onClick={onDiscover} disabled={busy || !url}
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300"
      data-testid="import-discover-btn">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
      {mode === 'sitemap' ? 'Import from sitemap' : 'Import products'}
    </button>
  </div>
);

// =====================================================================
// STEP 2 — Extracting (live progress)
// =====================================================================
const ExtractingStep = ({ steps, extracted, total, error, onBack, onRetry }) => {
  const pct = total > 0 ? Math.min(100, Math.round((extracted / total) * 100)) : 0;
  return (
    <div className="px-6 py-7 h-full flex flex-col items-center justify-center">
      <div className="relative mb-5">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
          error ? 'bg-red-100' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
          {error
            ? <AlertCircle className="w-7 h-7 text-red-600" />
            : <Sparkles className="w-7 h-7 text-white" />}
        </div>
        {!error && (
          <motion.div
            className="absolute -inset-2 rounded-full border-2 border-blue-200 border-t-blue-500"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          />
        )}
      </div>

      <h3 className="text-base font-semibold text-slate-900 mb-1">
        {error ? 'Extraction couldn\'t finish' : 'Hang tight — we\'re reading your site'}
      </h3>
      {!error && (
        <p className="text-xs text-slate-500 mb-5">
          This usually takes a few seconds per product
        </p>
      )}

      {!error && total > 0 && (
        <div className="w-full max-w-sm mb-5" data-testid="extract-progress-bar">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
            <span>Products extracted</span>
            <span className="font-medium tabular-nums">{extracted} / {total}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
      )}

      <ul className="w-full max-w-sm space-y-2.5" data-testid="extract-progress-steps">
        <AnimatePresence initial={false}>
          {steps.map((s) => (
            <motion.li
              key={s.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex items-start gap-2.5 text-sm">
              <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                s.status === 'done' ? 'bg-emerald-500'
                : s.status === 'active' ? 'bg-blue-500'
                : s.status === 'error' ? 'bg-red-500'
                : 'bg-slate-200'}`}>
                {s.status === 'done' && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                {s.status === 'active' && <Loader2 className="w-2.5 h-2.5 text-white animate-spin" />}
                {s.status === 'error' && <X className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </div>
              <span className={`leading-tight ${
                s.status === 'pending' ? 'text-slate-400'
                : s.status === 'error' ? 'text-red-600'
                : s.status === 'done' ? 'text-slate-600'
                : 'text-slate-900 font-medium'}`}>
                {s.text}
              </span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      {error && (
        <div className="mt-5 w-full max-w-sm">
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-3">
            {error}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={onBack}
              className="px-4 py-1.5 border border-slate-200 rounded-md text-xs hover:bg-slate-50"
              data-testid="extract-back-btn">
              Back
            </button>
            <button onClick={onRetry}
              className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
              data-testid="extract-retry-btn">
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// STEP 3 — Review (premium split-view)
// =====================================================================
const ReviewStep = ({ product, idx, total, errors, valid, busy, onUpdate, onPrev, onNext, onSkip, onSave, onFinish }) => (
  <div className="h-full flex flex-col">
    {/* Top: position, valid status, finish */}
    <div className="px-5 py-2 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <span className="font-medium">{product.product_name || 'Untitled product'}</span>
        {product._saved && (
          <span className="inline-flex items-center gap-1 text-emerald-600">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved
          </span>
        )}
      </div>
      <button onClick={onFinish}
        className="text-xs text-slate-500 hover:text-slate-900 font-medium"
        data-testid="import-finish-btn">
        Finish review →
      </button>
    </div>

    {/* Body: photos | form */}
    <div className="grid grid-cols-1 md:grid-cols-[40%_60%] flex-1 overflow-hidden min-h-0 relative">
      <PhotosPanel product={product} onUpdate={onUpdate} validationError={errors.tryon_image_url} />
      <InventoryProductForm product={product} onUpdate={onUpdate} errors={errors} />

      {/* Floating prev/next arrows */}
      {idx > 0 && (
        <button onClick={onPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-md hover:bg-slate-50 transition-all z-10"
          data-testid="import-prev-btn">
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      {idx < total - 1 && (
        <button onClick={onNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-md hover:bg-slate-50 transition-all z-10"
          data-testid="import-next-btn">
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>

    {/* Bottom action bar */}
    <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-slate-50">
      <div className="text-xs text-slate-500">
        {idx + 1} / {total}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onSkip} disabled={busy}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50"
          data-testid="import-skip-btn">
          Skip
        </button>
        <button onClick={onSave} disabled={busy || !valid || product._saved}
          className="inline-flex items-center gap-1.5 px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          data-testid="import-save-btn"
          title={!valid ? 'Fill all required fields' : undefined}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {product._saved ? 'Saved' : `Save (${SAVE_COST_PER_PRODUCT} credits)`}
        </button>
      </div>
    </div>
  </div>
);

// =====================================================================
// STEP 4 — Done summary
// =====================================================================
const DoneStep = ({ summary, onView, onClose }) => (
  <div className="px-5 py-8 text-center space-y-4">
    <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
    </div>
    <div>
      <p className="text-base font-semibold text-slate-900">
        Import complete — {summary.saved} product{summary.saved === 1 ? '' : 's'} saved
      </p>
      {summary.failed > 0 && (
        <p className="text-xs text-red-600 mt-1">{summary.failed} failed</p>
      )}
      <p className="text-xs text-slate-500 mt-1">
        Total credits used: <b>{summary.credits.toFixed(2)}</b>
      </p>
    </div>
    <div className="flex items-center justify-center gap-2">
      <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">
        Close
      </button>
      <button onClick={onView} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        data-testid="import-done-btn">
        View inventory
      </button>
    </div>
  </div>
);

export default ImportFromUrlModal;
